'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid'
import Button from '@/components/Button'
import Logo from '../../../../public/LAL_logo.svg'

const Header = ({ headerItems }) => {
    const router = useRouter()
    const pathname = usePathname()
    const [isOpen, setIsOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    // Handle Scroll Effect
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Close mobile menu when route changes
    useEffect(() => {
        setIsOpen(false)
    }, [pathname])

    return (
        <header 
            className={`site-header fixed top-0 left-0 right-0 z-[100] transition-all duration-500 border-b ${
                scrolled || isOpen
                    ? 'bg-black/85 backdrop-blur-md border-secondary/30 shadow-lg shadow-black/50 py-3' 
                    : 'bg-transparent border-transparent py-5'
            }`}
        >
            <div className="container mx-auto px-6 lg:px-12">
                <div className="flex items-center justify-between">
                    
                    {/* --- LEFT: LOGO --- */}
                    <div 
                        className="flex items-center gap-3 cursor-pointer group" 
                        onClick={() => router.push('/')}
                    >
                        <div className="relative w-10 h-10 lg:w-12 lg:h-12 transition-transform duration-500 group-hover:scale-105">
                            <Image 
                                src={Logo} 
                                alt="Legacy Asset Leaders" 
                                fill 
                                className="object-contain"
                                sizes="(max-width: 768px) 40px, 48px"
                            />
                        </div>
                        {/* Text: Hidden on Mobile, Visible on Desktop */}
                        <h3 className="hidden lg:block text-xl xl:text-2xl font-bold text-white tracking-wide uppercase empire-text">
                            Legacy Asset Leaders
                        </h3>
                    </div>

                    {/* --- CENTER: DESKTOP NAVIGATION --- */}
                    <nav className="hidden lg:flex items-center gap-8">
                        {headerItems?.map((item) => (
                            <Link 
                                key={item.label} 
                                href={item.href} 
                                className={`relative text-sm uppercase tracking-wider font-medium transition-colors duration-300 ${
                                    pathname === item.href ? 'text-secondary' : 'text-gray-300 hover:text-white'
                                }`}
                            >
                                {item.label}
                                {/* Gold Underline Animation */}
                                <span className={`absolute -bottom-1 left-0 w-full h-[1px] bg-secondary transform transition-transform duration-300 origin-center ${
                                    pathname === item.href ? 'scale-x-100' : 'scale-x-0 hover:scale-x-100'
                                }`} />
                            </Link>
                        ))}
                    </nav>

                    {/* --- RIGHT: CTA & MOBILE TOGGLE --- */}
                    <div className="flex items-center gap-4">
                        {/* CTA Button - Hidden on very small screens, visible on tablet+ */}
                        <div className="hidden sm:block">
                            <Link href="#contact">
                                <Button variant="primary" className="!py-2 !px-6 !text-sm">
                                    Book Now
                                </Button>
                            </Link>
                        </div>

                        {/* Mobile Menu Toggle */}
                        <button 
                            className="lg:hidden text-white hover:text-secondary transition-colors"
                            onClick={() => setIsOpen(!isOpen)}
                            aria-label="Toggle Menu"
                        >
                            {isOpen ? (
                                <XMarkIcon className="w-8 h-8" />
                            ) : (
                                <Bars3Icon className="w-8 h-8" />
                            )}
                        </button>
                    </div>
                </div>

                {/* --- MOBILE MENU DROPDOWN --- */}
                <div 
                    className={`lg:hidden grid transition-[grid-template-rows] duration-500 ease-out ${
                        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                >
                    <div className="overflow-hidden">
                        <div className="flex flex-col items-center gap-6 py-8 border-t border-white/10 mt-4">
                            {headerItems?.map((item) => (
                                <Link
                                    key={item.label}
                                    href={item.href}
                                    className="text-lg font-medium text-gray-200 hover:text-secondary transition-colors uppercase tracking-widest"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {item.label}
                                </Link>
                            ))}
                            {/* Mobile CTA (shown if hidden in header) */}
                            <div className="sm:hidden w-full px-8 pt-4">
                                <Link href="#contact" className="w-full">
                                    <Button className="w-full justify-center">
                                        Book Consultation
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    )
}

export default Header